import { userViewerSettingsAtom } from '@/atoms/user.atom';
import { colours } from '@/core';
import { getResourcesEndpoint } from '@/core/resources';
import { GoogleImagesByPositionQuery } from '@/graphql/graphql';
import { useLoader, useThree } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { MinimalHotspot } from './MinimalHotspot';

/**
 * Custom texture loader that uses fetch with credentials.
 */
/** Set from the renderer at mount, before the first texture load. */
let maxAnisotropy = 1;

class CredentialsTextureLoader extends THREE.Loader<THREE.Texture> {
  load(
    url: string,
    onLoad?: (texture: THREE.Texture) => void,
    _onProgress?: (event: ProgressEvent) => void,
    _onError?: (err: unknown) => void,
  ): THREE.Texture {
    const texture = new THREE.Texture();

    // Public paths (e.g. google-streetview) don't need credentials
    const isPublic = new URL(url).pathname.startsWith('/public/');
    const fetchOptions: RequestInit = isPublic ? {} : { credentials: 'include' };

    fetch(url, fetchOptions)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const contentType = res.headers.get('content-type') || '';
        // If the API returns JSON with a redirect URL, follow it without credentials
        if (contentType.includes('application/json')) {
          const data = await res.json();
          if (data.url) return fetch(data.url);
          throw new Error('Unexpected JSON response');
        }
        return res;
      })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => createImageBitmap(blob, { imageOrientation: 'flipY' }))
      .then((bitmap) => {
        texture.image = bitmap;
        // Set before the first upload: three bakes colour space and sampler
        // state in on upload and never re-reads them without a version bump,
        // so setting them in an effect lets a frame through washed out.
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.repeat.x = -1;
        texture.offset.x = 1;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.generateMipmaps = true;
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.anisotropy = maxAnisotropy;
        texture.needsUpdate = true;
        onLoad?.(texture);
      })
      .catch((err) => {
        console.warn(`[GoogleStreetView] Failed to load texture: ${url}`, err);
        _onError?.(err);
      });

    return texture;
  }
}

const GoogleStreetView = ({
  setPosition,
  imageId,
  setImageId,
  position,
  hoverImageId,
  setHoverImageId,
  googleImages,
  positionWGS84,
  googleImage,
}: {
  googleImages: (GoogleImagesByPositionQuery['images'][number] & {
    location: { coords: [number, number, number]; srid: string };
  })[];
  positionWGS84: THREE.Vector3 | null;
  setPosition: Dispatch<SetStateAction<THREE.Vector3>>;
  imageId: string | null;
  googleImage:
    | (GoogleImagesByPositionQuery['images'][number] & {
        location: { coords: [number, number, number]; srid: string };
      })
    | null;
  setImageId: Dispatch<SetStateAction<string | null>>;
  position: THREE.Vector3;
  projection: string;
  hoverImageId: string | null;
  setHoverImageId: Dispatch<SetStateAction<string | null>>;
}) => {
  const gl = useThree((state) => state.gl);
  // Before useLoader below, so the loader bakes it into the first upload.
  useMemo(() => {
    maxAnisotropy = Math.min(8, gl.capabilities.getMaxAnisotropy());
  }, [gl]);
  const canvas = useThree((state) => state.gl.domElement);
  const viewerSettings = useAtomValue(userViewerSettingsAtom);
  const googleStreetViewPoiColor = viewerSettings.googleStreetViewPoiColor || colours.images.default;

  useEffect(() => {
    if (!googleImages || googleImages.length === 0) return setImageId(null);
    if (!positionWGS84) return;

    if (googleImage) return;

    // Find the closest image to the current position
    let closestImagePosition = googleImages[0];
    let closestDistance = Infinity;

    googleImages.forEach((googleImage) => {
      const distance = positionWGS84.distanceTo(new THREE.Vector3(...googleImage.locationWGS84.coords)) ?? Infinity;
      if (distance < closestDistance) {
        closestDistance = distance;
        closestImagePosition = googleImage;
      }
    });

    setImageId(closestImagePosition.id);
  }, [position, googleImages, imageId, setImageId]);

  const textures = useLoader(
    CredentialsTextureLoader,
    googleImage
      ? [
          googleImage.root.startsWith('https://')
            ? googleImage.root
            : `${getResourcesEndpoint()}/${googleImage.root}`,
        ]
      : [],
  );

  useEffect(() => {
    if (!imageId || !googleImage) return;

    if (googleImage.location.coords[0] === position.x && googleImage.location.coords[1] === position.y) return;

    setPosition(new THREE.Vector3(googleImage.location.coords[0], googleImage.location.coords[1], position.z));
  }, [imageId, googleImage, setPosition]);


  const sphereGeom = useMemo(() => {
    const sphereGeom = new THREE.SphereGeometry(10, 64, 32);
    sphereGeom.rotateX(Math.PI / 2);
    // sphereGeom.scale(-1, 1, 1);
    return sphereGeom;
  }, [textures]);

  const rotation = useMemo(() => {
    if (!googleImage) return new THREE.Quaternion();

    // Google's definitions:
    // - heading: 0-360°, clockwise from North (0° = North)
    // - tilt: 0-180° from south pole to horizon (90° = horizon at equator, which is correct)
    // - roll: 0-360°, clockwise rotation around line of sight to level horizon

    // Convert to radians
    const heading = THREE.MathUtils.degToRad(-googleImage.heading + 90);
    const tilt = THREE.MathUtils.degToRad(googleImage.tilt - 90); // Offset so 0° is horizon
    const roll = THREE.MathUtils.degToRad(googleImage.roll);

    // For Z-up coordinate system:
    // - Heading rotates around Z axis (up)
    // - Tilt rotates around local Y axis (pitch)
    // - Roll rotates around local X axis (line of sight)

    const qHeading = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 0, 1),
      heading, // Negative because Google uses clockwise, Three.js uses counter-clockwise
    );

    const qTilt = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      -tilt, // Pitch down/up
    );

    const qRoll = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(1, 0, 0),
      -roll, // Roll to level horizon
    );

    // Apply in order: Heading (yaw) -> Tilt (pitch) -> Roll
    const finalQuaternion = new THREE.Quaternion();
    finalQuaternion.multiply(qHeading);
    finalQuaternion.multiply(qTilt);
    finalQuaternion.multiply(qRoll);

    return finalQuaternion;
  }, [googleImage?.heading, googleImage?.tilt, googleImage?.roll]);

  if (!googleImage) return null;

  return (
    <group>
      <mesh position={position.clone()} renderOrder={-9999} quaternion={rotation}>
        <primitive object={sphereGeom} />
        <meshBasicMaterial
          map={textures[0]}
          side={THREE.BackSide}
          depthWrite={false}
          depthTest={false}
          toneMapped={false}
          fog={false}
        />
      </mesh>
      {googleImages.map((img) => (
        <MinimalHotspot
          key={img.id}
          position={new THREE.Vector3(img.location.coords[0], img.location.coords[1], position.z - 1.5)}
          active={imageId === img.id}
          hovered={hoverImageId === img.id}
          colorDefault={googleStreetViewPoiColor}
          colorHover={colours.images.hover}
          colorActive={colours.images.active}
          onPointerEnter={() => {
            if (imageId === img.id) return;
            setHoverImageId(img.id);
            canvas.style.cursor = 'pointer';
          }}
          onPointerLeave={() => {
            setHoverImageId(null);
            canvas.style.cursor = 'auto';
          }}
          onClick={(e) => {
            e.stopPropagation();
            setImageId(img.id);
          }}
        />
      ))}
    </group>
  );
};

export default GoogleStreetView;
